import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Tabs, TabPanel, useTabs } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { useContact, useConvertContact, useDeleteContact } from './hooks/useContacts';
import { Dialog } from '@/components/ui/Dialog';
import {
  useContactNotes,
  useCreateNote,
  useContactReminders,
  useCreateReminder,
  useCompleteReminder,
  type ContactNote,
  type Reminder,
} from './hooks/useNotesReminders';
import { useNoteCategories } from './hooks/useNoteCategories';
import { useStaffUsers } from './hooks/useStaffUsers';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { useToast } from '@/components/ui/Toast';
import { useResolveFormForEntityType } from '@/features/form-builder/hooks/useFormBuilder';
import type { FormSection, FormField } from '@/features/form-builder/hooks/useFormBuilder';
import { useLookupValues } from '@/hooks/useLookupValues';
import {
  useDocumentsList,
  useUploadDocument,
  useDeleteDocument,
  type Document as DocumentItem,
} from '@/features/documents/hooks/useDocuments';
import { apiClient, type ApiResponse } from '@/lib/api-client';
import { useSystemLookups } from '@/features/settings/hooks/useSystemLookups';
import { useJobsList } from '@/features/jobs/hooks/useJobs';
import { ContactBillingTab } from './ContactBillingTab';
import { ContactGroupsTab } from './ContactGroupsTab';
import { NotificationSettingsTab } from './NotificationSettingsTab';
import { usePortalStatus, useAssignPortal, useCancelPortal } from './hooks/usePortalAccess';
import { ContactFolderStructure } from './ContactFolderStructure';

/**
 * ContactDetailPage — displays contact details with tabs for details, activity, documents, and jobs.
 * Requirements: 5.1, 5.5
 */
export function ContactDetailPage() {
  const { contactId: id } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { activeTab, onChange } = useTabs('details');

  const { data: contact, isLoading } = useContact(id ?? '');
  const { data: reminders } = useContactReminders(id ?? '');
  const convertMutation = useConvertContact();
  const deleteMutation = useDeleteContact();
  const [showArchive, setShowArchive] = useState(false);
  const { toast } = useToast();

  // Portal access management
  const { data: portalStatus } = usePortalStatus(id ?? '');
  const assignPortalMutation = useAssignPortal();
  const cancelPortalMutation = useCancelPortal();

  // Load the form definition for this contact's entity type
  const entityTypeId = contact?.entityTypeId ?? '';
  const { data: formDefinition } = useResolveFormForEntityType(entityTypeId);

  // Contact nature is available directly from the contact record
  const isProspectNature = contact?.contactNature === 'prospect';
  const isClientNature = contact?.contactNature === 'client';

  // Fetch contact categories for display label resolution
  const { data: contactCategories } = useSystemLookups('contact_categories');

  // Ensure first available tab is selected on load
  const firstAvailableTabId = useMemo(() => {
    const sections = formDefinition?.sections ?? [];
    const formTabs = sections.filter((s) => s.render_as === 'tab' && !s.parent_tab_id);
    const orphans = sections.filter(
      (s) => s.render_as !== 'tab' && !s.parent_tab_id && s.is_active,
    );
    if (formTabs.length > 0) return `section-${formTabs[0].id}`;
    if (orphans.length > 0) return 'form-details';
    return 'tags';
  }, [formDefinition]);

  useEffect(() => {
    onChange(firstAvailableTabId);
  }, [firstAvailableTabId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-2 text-gray-500" aria-live="polite">
          <svg
            className="h-5 w-5 animate-spin text-brand-500"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          Loading contact...
        </div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="py-12 text-center text-gray-500 dark:text-gray-400">Contact not found.</div>
    );
  }

  const fullName = `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();

  // Build dynamic tabs from form definition — same approach as ContactForm
  const allSections = formDefinition?.sections ?? [];
  const formTabSections = allSections.filter((s) => s.render_as === 'tab' && !s.parent_tab_id);
  const orphanSections = allSections.filter(
    (s) => s.render_as !== 'tab' && !s.parent_tab_id && s.is_active,
  );
  const hasOrphans = orphanSections.length > 0;

  const pendingReminderCount = reminders?.filter((r) => !r.isCompleted).length ?? 0;

  const tabs = [
    ...(formTabSections.length > 0
      ? formTabSections.map((s) => ({ id: `section-${s.id}`, label: s.name }))
      : hasOrphans
        ? [{ id: 'form-details', label: 'Details' }]
        : []),
    { id: 'tags', label: 'Tags' },
    ...(can(PERMISSIONS.NOTE_CATEGORIES_READ) ? [{ id: 'notes', label: 'Notes' }] : []),
    ...(can(PERMISSIONS.NOTE_CATEGORIES_READ)
      ? [
          {
            id: 'reminders',
            label: 'Reminders',
            badge:
              pendingReminderCount > 0 ? (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-100 px-1.5 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-400">
                  {pendingReminderCount}
                </span>
              ) : undefined,
          },
        ]
      : []),
    { id: 'activity', label: 'Activity' },
    ...(can(PERMISSIONS.DOCUMENTS_READ) ? [{ id: 'documents', label: 'Documents' }] : []),
    ...(can(PERMISSIONS.GROUPS_READ) ? [{ id: 'groups', label: 'Groups' }] : []),
    { id: 'jobs', label: 'Jobs' },
    ...(can(PERMISSIONS.BILLING_READ) ? [{ id: 'billing', label: 'Billing' }] : []),
    ...(can(PERMISSIONS.CONTACTS_MANAGE_NOTIFICATIONS)
      ? [{ id: 'notifications', label: 'Notification Settings' }]
      : []),
  ];

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate('/contacts')}
        className="text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400"
      >
        &larr; Back to Contacts
      </button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{fullName}</h1>
            <Badge
              variant={isClientNature ? 'success' : isProspectNature ? 'warning' : 'default'}
              dot
            >
              {contactCategories?.find((c) => c.code === contact.contactType)?.label ??
                (contact.contactType
                  ? contact.contactType.charAt(0).toUpperCase() + contact.contactType.slice(1)
                  : 'Unknown')}
            </Badge>
            <Badge variant={contact.entityType === 'business' ? 'info' : 'default'} size="sm">
              {contact.entityType === 'business' ? 'Business' : 'Individual'}
            </Badge>
            {contact.clientCode && (
              <span
                className="rounded bg-brand-50 px-2 py-0.5 font-mono text-xs font-medium text-brand-700 dark:bg-brand-900/20 dark:text-brand-300"
                title="Client code"
              >
                {contact.clientCode}
              </span>
            )}
          </div>
          {contact.businessName && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{contact.businessName}</p>
          )}
          {contact.email && (
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{contact.email}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isProspectNature && can(PERMISSIONS.CLIENTS_CONVERT) && (
            <Button
              variant="outline"
              loading={convertMutation.isPending}
              onClick={async () => {
                if (!id) return;
                try {
                  await convertMutation.mutateAsync(id);
                  toast({ variant: 'success', title: 'Contact converted to client' });
                  navigate('/contacts');
                } catch (err: unknown) {
                  const error = err as {
                    response?: {
                      data?: {
                        error?: {
                          message?: string;
                          details?: {
                            missingFields?: Array<{ key: string; label: string }> | string[];
                          };
                        };
                      };
                    };
                  };
                  const missingFields = error?.response?.data?.error?.details?.missingFields;
                  if (missingFields && missingFields.length > 0) {
                    const labels = missingFields.map((f) => (typeof f === 'string' ? f : f.label));
                    toast({
                      variant: 'error',
                      title: 'Cannot convert to client',
                      description: `Please fill in the required fields: ${labels.join(', ')}`,
                    });
                  } else {
                    toast({
                      variant: 'error',
                      title: error?.response?.data?.error?.message || 'Failed to convert contact',
                    });
                  }
                }
              }}
            >
              Convert to Client
            </Button>
          )}
          {isClientNature &&
            can(PERMISSIONS.CLIENTS_MANAGE_PORTAL) &&
            portalStatus &&
            !portalStatus.hasPortalAccess && (
              <Button
                variant="outline"
                loading={assignPortalMutation.isPending}
                onClick={async () => {
                  if (!id) return;
                  try {
                    const result = await assignPortalMutation.mutateAsync(id);
                    toast({
                      variant: 'success',
                      title: result.message || 'Portal access assigned',
                      description: 'An invitation email has been sent to the client.',
                    });
                  } catch (err: unknown) {
                    const error = err as { response?: { data?: { error?: { message?: string } } } };
                    toast({
                      variant: 'error',
                      title:
                        error?.response?.data?.error?.message || 'Failed to assign portal access',
                    });
                  }
                }}
              >
                {portalStatus.portalUserStatus === 'suspended'
                  ? 'Reactivate Portal'
                  : 'Assign Portal'}
              </Button>
            )}
          {isClientNature &&
            can(PERMISSIONS.CLIENTS_MANAGE_PORTAL) &&
            portalStatus &&
            portalStatus.hasPortalAccess && (
              <Button
                variant="outline"
                loading={cancelPortalMutation.isPending}
                onClick={async () => {
                  if (!id) return;
                  try {
                    await cancelPortalMutation.mutateAsync(id);
                    toast({
                      variant: 'success',
                      title: 'Portal access revoked',
                      description: 'The client can no longer access the portal.',
                    });
                  } catch (err: unknown) {
                    const error = err as { response?: { data?: { error?: { message?: string } } } };
                    toast({
                      variant: 'error',
                      title:
                        error?.response?.data?.error?.message || 'Failed to cancel portal access',
                    });
                  }
                }}
              >
                Cancel Portal Access
              </Button>
            )}
          {can(PERMISSIONS.CONTACTS_UPDATE) && (
            <Button onClick={() => navigate(`/contacts/${id}/edit`)}>Edit</Button>
          )}
          {can(PERMISSIONS.CONTACTS_DELETE) && (
            <Button variant="danger" onClick={() => setShowArchive(true)}>
              Archive/Delete
            </Button>
          )}
        </div>
      </div>

      {/* Archive / delete confirmation */}
      <Dialog
        open={showArchive}
        onClose={() => setShowArchive(false)}
        onConfirm={() =>
          deleteMutation.mutate(id!, {
            onSuccess: () => {
              toast({ variant: 'success', title: 'Contact archived' });
              setShowArchive(false);
              navigate('/contacts');
            },
            onError: () => toast({ variant: 'error', title: 'Failed to archive contact' }),
          })
        }
        title={`Archive ${fullName || 'this contact'}?`}
        description="This archives the contact and hides it from active lists and search. Its history (jobs, billing, documents) is preserved and it can be restored by filtering for archived contacts."
        confirmLabel="Archive/Delete"
        confirmVariant="danger"
        loading={deleteMutation.isPending}
      />

      {/* Tags */}
      {contact.tags && contact.tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {contact.tags.map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
              style={{
                backgroundColor: tag.color ? `${tag.color}20` : undefined,
                color: tag.color || undefined,
                border: `1px solid ${tag.color || '#D1D5DB'}`,
              }}
              title={tag.description || tag.name}
            >
              {tag.color && (
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: tag.color }}
                />
              )}
              {tag.name}
            </span>
          ))}
        </div>
      )}

      {/* Lead Stage (for prospects) */}
      {isProspectNature && contact.leadStage && (
        <Card padding="sm">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
              Lead Stage:
            </span>
            <Badge variant="info">{contact.leadStage}</Badge>
            {contact.leadSource && (
              <>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  Source:
                </span>
                <Badge variant="default">{contact.leadSource}</Badge>
              </>
            )}
          </div>
        </Card>
      )}

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={onChange} />

      {/* Form Tab Panels — one per tab section */}
      {formTabSections.map((tab, tabIndex) => {
        const explicitChildren = allSections
          .filter((s) => s.parent_tab_id === tab.id && s.render_as !== 'tab' && s.is_active)
          .sort((a, b) => a.display_order - b.display_order);

        let implicitChildren: typeof allSections = [];
        if (explicitChildren.length === 0) {
          const nextTab = formTabSections[tabIndex + 1];
          implicitChildren = orphanSections.filter((s) => {
            const afterThisTab = s.display_order >= tab.display_order;
            const beforeNextTab = nextTab ? s.display_order < nextTab.display_order : true;
            return afterThisTab && beforeNextTab;
          });
        }

        const children = explicitChildren.length > 0 ? explicitChildren : implicitChildren;
        const tabHasFields = tab.fields.filter((f) => f.is_active).length > 0;

        const sectionsToRender = [
          ...(tabHasFields ? [{ ...tab, render_as: 'section' as const }] : []),
          ...children,
        ];

        return (
          <TabPanel key={tab.id} tabId={`section-${tab.id}`} activeTab={activeTab}>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {sectionsToRender.map((section) => (
                <DynamicDetailSection
                  key={section.id}
                  section={section}
                  contact={contact as unknown as { [key: string]: unknown }}
                />
              ))}
            </div>
          </TabPanel>
        );
      })}

      {/* Orphan sections (no tabs defined — backward compat) */}
      {formTabSections.length === 0 && hasOrphans && (
        <TabPanel tabId="form-details" activeTab={activeTab}>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {orphanSections.map((section) => (
              <DynamicDetailSection
                key={section.id}
                section={section}
                contact={contact as unknown as { [key: string]: unknown }}
              />
            ))}
          </div>
        </TabPanel>
      )}

      {/* Tags Tab */}
      <TabPanel tabId="tags" activeTab={activeTab}>
        <Card>
          <CardHeader title="Tags" />
          <CardContent>
            {contact.tags && contact.tags.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {contact.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
                    style={{
                      backgroundColor: tag.color ? `${tag.color}20` : undefined,
                      color: tag.color || undefined,
                      border: `1px solid ${tag.color || '#D1D5DB'}`,
                    }}
                  >
                    {tag.color && (
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                    )}
                    {tag.name}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No tags assigned.</p>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Notes Tab */}
      {can(PERMISSIONS.NOTE_CATEGORIES_READ) && (
        <TabPanel tabId="notes" activeTab={activeTab}>
          <NotesTabContent contactId={id!} />
        </TabPanel>
      )}

      {/* Reminders Tab */}
      {can(PERMISSIONS.NOTE_CATEGORIES_READ) && (
        <TabPanel tabId="reminders" activeTab={activeTab}>
          <RemindersTabContent contactId={id!} />
        </TabPanel>
      )}

      {/* Activity Tab */}
      <TabPanel tabId="activity" activeTab={activeTab}>
        <ContactActivityLog contactId={id!} />
      </TabPanel>

      {/* Documents Tab */}
      <TabPanel tabId="documents" activeTab={activeTab}>
        <DocumentsTabContent contactId={id!} />
      </TabPanel>

      {/* Groups Tab (merged relationships) */}
      <TabPanel tabId="groups" activeTab={activeTab}>
        <ContactGroupsTab contactId={id!} />
      </TabPanel>

      {/* Jobs Tab */}
      <TabPanel tabId="jobs" activeTab={activeTab}>
        <ContactJobsTab contactId={id!} contactName={fullName} isClientNature={isClientNature} />
      </TabPanel>

      {/* Billing Tab */}
      {can(PERMISSIONS.BILLING_READ) && (
        <TabPanel tabId="billing" activeTab={activeTab}>
          <ContactBillingTab contactId={id!} />
        </TabPanel>
      )}

      {/* Notification Settings Tab — staff-controlled per-client notification gates */}
      {can(PERMISSIONS.CONTACTS_MANAGE_NOTIFICATIONS) && (
        <TabPanel tabId="notifications" activeTab={activeTab}>
          <NotificationSettingsTab contactId={id!} />
        </TabPanel>
      )}
    </div>
  );
}

// ─── Helper Components ────────────────────────────────────────────────────────

// ─── Contact Activity Log (scrollable + searchable) ───────────────────────────

function ContactActivityLog({ contactId }: { contactId: string }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['contact-activity', contactId, page, debouncedSearch],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', '30');
      if (debouncedSearch) params.set('search', debouncedSearch);
      const res = await apiClient.get<{
        success: boolean;
        data: {
          activities: Array<Record<string, unknown>>;
          pagination: { page: number; pageSize: number; total: number; totalPages: number };
        };
      }>(`/contacts/${contactId}/activity?${params.toString()}`);
      return res.data.data;
    },
    enabled: !!contactId,
  });

  const activities = data?.activities ?? [];
  const pagination = data?.pagination;

  return (
    <Card>
      <CardHeader title="Activity Timeline" />
      <CardContent>
        {/* Search */}
        <div className="relative mb-3">
          <svg
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search activity..."
            className="w-full rounded-lg border border-border bg-white py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-brand-500 focus:outline-none dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
          />
        </div>

        {/* Activity list with scroll */}
        <div className="max-h-[500px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
              <span className="ml-2 text-sm text-gray-500">Loading activity...</span>
            </div>
          ) : activities.length > 0 ? (
            <div className="space-y-3">
              {activities.map((entry) => (
                <div
                  key={entry.id as string}
                  className="flex gap-3 border-l-2 border-gray-200 pl-4 dark:border-gray-700"
                >
                  <div className="flex-1">
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {(entry.description as string) || `${entry.entity_type} ${entry.action}`}
                    </p>
                    <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      {!!entry.user_name && <span>{String(entry.user_name)}</span>}
                      {!!entry.user_name && <span>·</span>}
                      <time dateTime={String(entry.created_at)}>
                        {new Date(String(entry.created_at)).toLocaleString('en-CA', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </time>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
              {debouncedSearch ? 'No activity matching your search.' : 'No activity recorded yet.'}
            </p>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-3 flex items-center justify-between border-t border-border pt-3 text-xs text-gray-500 dark:border-border-dark dark:text-gray-400">
            <span>
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} entries)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded border border-border px-2 py-1 disabled:opacity-40 hover:bg-gray-50 dark:border-border-dark dark:hover:bg-surface-dark-secondary"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded border border-border px-2 py-1 disabled:opacity-40 hover:bg-gray-50 dark:border-border-dark dark:hover:bg-surface-dark-secondary"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Notes Tab Content ────────────────────────────────────────────────────────

function NotesTabContent({ contactId }: { contactId: string }) {
  const { can } = usePermissions();
  const { toast } = useToast();
  const { data: notes, isLoading: notesLoading } = useContactNotes(contactId);
  const { data: categories } = useNoteCategories();
  const { data: staffUsers } = useStaffUsers();
  const createNoteMutation = useCreateNote(contactId);

  const [showAddForm, setShowAddForm] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteCategory, setNoteCategory] = useState('');
  const [reminderDate, setReminderDate] = useState('');
  const [reminderAssigneeId, setReminderAssigneeId] = useState('');
  const [reminderAssigneeSearch, setReminderAssigneeSearch] = useState('');
  const [showReminderAssigneeDropdown, setShowReminderAssigneeDropdown] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');

  const canAddNotes = can(PERMISSIONS.CONTACTS_UPDATE) && can(PERMISSIONS.NOTE_CATEGORIES_CREATE);

  // Check if selected category is a scheduling category
  const selectedCategoryObj = categories?.find(
    (c) => c.name.toLowerCase().replace(/\s+/g, '_') === noteCategory,
  );
  const isSchedulingCategory = selectedCategoryObj?.isScheduling ?? false;

  const handleAddNote = useCallback(async () => {
    if (!noteContent.trim()) return;
    if (isSchedulingCategory && !reminderDate) {
      toast({ variant: 'error', title: 'Reminder date is required for this category' });
      return;
    }

    try {
      await createNoteMutation.mutateAsync({
        content: noteContent.trim(),
        category: noteCategory || 'general',
        reminderDate:
          isSchedulingCategory && reminderDate ? new Date(reminderDate).toISOString() : undefined,
        reminderAssigneeId:
          isSchedulingCategory && reminderAssigneeId ? reminderAssigneeId : undefined,
      });
      setNoteContent('');
      setNoteCategory('');
      setReminderDate('');
      setReminderAssigneeId('');
      setReminderAssigneeSearch('');
      setShowAddForm(false);
      toast({ variant: 'success', title: 'Note added successfully' });
    } catch {
      toast({ variant: 'error', title: 'Failed to add note' });
    }
  }, [
    noteContent,
    noteCategory,
    reminderDate,
    reminderAssigneeId,
    isSchedulingCategory,
    createNoteMutation,
    toast,
  ]);

  // Filter notes by category
  const filteredNotes = filterCategory
    ? notes?.filter((n) => n.category === filterCategory)
    : notes;

  const getCategoryLabel = (categoryValue: string) => {
    const cat = categories?.find(
      (c) => c.name.toLowerCase().replace(/\s+/g, '_') === categoryValue,
    );
    return cat?.name ?? categoryValue.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getCategoryColor = (categoryValue: string) => {
    const cat = categories?.find(
      (c) => c.name.toLowerCase().replace(/\s+/g, '_') === categoryValue,
    );
    return cat?.color ?? null;
  };

  return (
    <Card>
      <CardHeader
        title="Notes"
        action={
          canAddNotes ? (
            <Button size="sm" variant="outline" onClick={() => setShowAddForm((prev) => !prev)}>
              {showAddForm ? 'Cancel' : 'Add Note'}
            </Button>
          ) : undefined
        }
      />
      <CardContent>
        {/* Category filter */}
        {categories && categories.length > 0 && (
          <div className="mb-4">
            <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
              Filter by category
            </label>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setFilterCategory('')}
                className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  !filterCategory
                    ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setFilterCategory(cat.name.toLowerCase().replace(/\s+/g, '_'))}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                    filterCategory === cat.name.toLowerCase().replace(/\s+/g, '_')
                      ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  {cat.color && (
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: cat.color }}
                      aria-hidden="true"
                    />
                  )}
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Add Note Form */}
        {showAddForm && canAddNotes && (
          <div className="mb-4 rounded-lg border border-border p-4 dark:border-border-dark">
            <div className="space-y-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Category
                </label>
                <select
                  value={noteCategory}
                  onChange={(e) => setNoteCategory(e.target.value)}
                  className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="">Select category</option>
                  {categories?.map((cat) => (
                    <option key={cat.id} value={cat.name.toLowerCase().replace(/\s+/g, '_')}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Note Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  rows={4}
                  maxLength={10000}
                  placeholder="Type your note here..."
                  className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400"
                />
              </div>
              {isSchedulingCategory && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Reminder Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={reminderDate}
                    onChange={(e) => setReminderDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    A reminder will be created and triggered on this date.
                  </p>
                </div>
              )}
              {isSchedulingCategory && (
                <div className="relative">
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Assign Reminder To
                  </label>
                  <input
                    type="text"
                    value={reminderAssigneeSearch}
                    onChange={(e) => {
                      setReminderAssigneeSearch(e.target.value);
                      setShowReminderAssigneeDropdown(true);
                      if (!e.target.value.trim()) setReminderAssigneeId('');
                    }}
                    onFocus={() => setShowReminderAssigneeDropdown(true)}
                    placeholder="Search user (leave empty for yourself)..."
                    className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400"
                    autoComplete="off"
                  />
                  {showReminderAssigneeDropdown && staffUsers && staffUsers.length > 0 && (
                    <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-border bg-white shadow-lg dark:border-border-dark dark:bg-gray-800">
                      {(staffUsers ?? [])
                        .filter((u) => {
                          if (!reminderAssigneeSearch.trim()) return true;
                          const q = reminderAssigneeSearch.toLowerCase();
                          return (
                            u.email.toLowerCase().includes(q) || u.name.toLowerCase().includes(q)
                          );
                        })
                        .slice(0, 20)
                        .map((user) => (
                          <li key={user.id}>
                            <button
                              type="button"
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                              onClick={() => {
                                setReminderAssigneeId(user.id);
                                setReminderAssigneeSearch(user.name || user.email);
                                setShowReminderAssigneeDropdown(false);
                              }}
                            >
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {user.name || user.email}
                              </span>
                              {user.name && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {user.email}
                                </span>
                              )}
                            </button>
                          </li>
                        ))}
                    </ul>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Optional. If left empty, the reminder will be assigned to you.
                  </p>
                </div>
              )}
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={handleAddNote}
                  loading={createNoteMutation.isPending}
                  disabled={!noteContent.trim()}
                >
                  Save Note
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Notes List */}
        {notesLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading notes...</p>
        ) : filteredNotes && filteredNotes.length > 0 ? (
          <ul role="list" className="divide-y divide-border dark:divide-border-dark">
            {filteredNotes.map((note) => (
              <NoteListItem
                key={note.id}
                note={note}
                categoryLabel={getCategoryLabel(note.category)}
                categoryColor={getCategoryColor(note.category)}
              />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {filterCategory
              ? 'No notes in this category.'
              : 'No notes yet. Add a note to keep track of interactions.'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function NoteListItem({
  note,
  categoryLabel,
  categoryColor,
}: {
  note: ContactNote;
  categoryLabel: string;
  categoryColor: string | null;
}) {
  const date = new Date(note.createdAt);
  const formattedDate = date.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="default" size="sm">
              <span className="inline-flex items-center gap-1">
                {categoryColor && (
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: categoryColor }}
                    aria-hidden="true"
                  />
                )}
                {categoryLabel}
              </span>
            </Badge>
            {note.createdByName && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                by {note.createdByName}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
            {note.content}
          </p>
        </div>
        <time
          dateTime={note.createdAt}
          className="shrink-0 text-xs text-gray-400 dark:text-gray-500"
        >
          {formattedDate}
        </time>
      </div>
    </li>
  );
}

// ─── Documents Tab Content ────────────────────────────────────────────────────

function DocumentsTabContent({ contactId }: { contactId: string }) {
  const { can } = usePermissions();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: documentsData, isLoading } = useDocumentsList({
    clientId: contactId,
    includeLinked: true,
  });
  const uploadDocument = useUploadDocument();
  const deleteDocument = useDeleteDocument();

  const documents = documentsData?.items ?? [];

  const handleUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        await uploadDocument.mutateAsync({ file, clientId: contactId });
        toast({ variant: 'success', title: 'Document uploaded' });
      } catch {
        toast({ variant: 'error', title: 'Failed to upload document' });
      }

      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [contactId, uploadDocument, toast],
  );

  const handleDelete = useCallback(
    async (doc: DocumentItem) => {
      if (!window.confirm(`Delete "${doc.fileName}"? This cannot be undone.`)) return;
      try {
        await deleteDocument.mutateAsync(doc.id);
        toast({ variant: 'success', title: 'Document deleted' });
      } catch {
        toast({ variant: 'error', title: 'Failed to delete document' });
      }
    },
    [deleteDocument, toast],
  );

  const handleDownload = useCallback(
    async (doc: DocumentItem) => {
      try {
        const response = await apiClient.get(`/documents/${doc.id}/download`, {
          responseType: 'blob',
        });
        const blob = new Blob([response.data as BlobPart]);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        toast({ variant: 'error', title: 'Failed to download document' });
      }
    },
    [toast],
  );

  const handlePreview = useCallback(
    async (doc: DocumentItem) => {
      try {
        const response = await apiClient.get(`/documents/${doc.id}/preview`, {
          responseType: 'blob',
        });
        const blob = new Blob([response.data as BlobPart], {
          type: doc.fileType || 'application/octet-stream',
        });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      } catch {
        toast({ variant: 'error', title: 'Failed to preview document' });
      }
    },
    [toast],
  );

  return (
    <div className="space-y-6">
      <ContactFolderStructure contactId={contactId} />
      <Card>
        <CardHeader
          title="Documents"
          action={
            can(PERMISSIONS.DOCUMENTS_UPLOAD) ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleUpload}
                  className="hidden"
                  aria-label="Upload document file"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  loading={uploadDocument.isPending}
                >
                  Upload Document
                </Button>
              </>
            ) : undefined
          }
        />
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading documents...</p>
          ) : documents.length > 0 ? (
            <ul role="list" className="divide-y divide-border dark:divide-border-dark">
              {documents.map((doc) => (
                <li key={doc.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <DocumentIcon fileType={doc.fileType} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {doc.fileName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatFileSize(doc.fileSize)}
                        {' · '}
                        {new Date(doc.createdAt).toLocaleDateString('en-CA', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                        {doc.scanStatus === 'infected' && (
                          <span className="ml-2 text-red-600 dark:text-red-400 font-medium">
                            Malware detected
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {can(PERMISSIONS.DOCUMENTS_READ) && (
                      <button
                        type="button"
                        onClick={() => handlePreview(doc)}
                        className="rounded p-1.5 text-gray-400 hover:text-brand-600 dark:hover:text-brand-400"
                        aria-label={`Preview ${doc.fileName}`}
                        title="Preview"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      </button>
                    )}
                    {can(PERMISSIONS.DOCUMENTS_DOWNLOAD) && (
                      <button
                        type="button"
                        onClick={() => handleDownload(doc)}
                        className="rounded p-1.5 text-gray-400 hover:text-brand-600 dark:hover:text-brand-400"
                        aria-label={`Download ${doc.fileName}`}
                        title="Download"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                      </button>
                    )}
                    {can(PERMISSIONS.DOCUMENTS_DELETE) && (
                      <button
                        type="button"
                        onClick={() => handleDelete(doc)}
                        className="rounded p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        aria-label={`Delete ${doc.fileName}`}
                        title="Delete"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No documents yet. Upload a document to get started.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DocumentIcon({ fileType }: { fileType: string }) {
  const isPdf = fileType.includes('pdf');
  const isImage = fileType.startsWith('image/');
  const isSpreadsheet = fileType.includes('sheet') || fileType.includes('csv');

  let color = 'text-gray-400';
  if (isPdf) color = 'text-red-400';
  else if (isImage) color = 'text-blue-400';
  else if (isSpreadsheet) color = 'text-green-400';

  return (
    <svg
      className={`h-8 w-8 shrink-0 ${color}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    </svg>
  );
}

function formatFileSize(bytes: number): string {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

// ─── Reminders Tab Content ────────────────────────────────────────────────────

function RemindersTabContent({ contactId }: { contactId: string }) {
  const { can } = usePermissions();
  const { data: reminders, isLoading } = useContactReminders(contactId);
  const completeReminder = useCompleteReminder(contactId);
  const createReminder = useCreateReminder(contactId);
  const { data: staffUsers } = useStaffUsers();
  const { toast } = useToast();

  const [showAddForm, setShowAddForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('09:00');
  const [assigneeId, setAssigneeId] = useState('');
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);

  const pendingReminders = reminders?.filter((r) => !r.isCompleted) ?? [];
  const completedReminders = reminders?.filter((r) => r.isCompleted) ?? [];

  const filteredUsers = useMemo(() => {
    if (!staffUsers) return [];
    if (!assigneeSearch.trim()) return staffUsers;
    const q = assigneeSearch.toLowerCase();
    return staffUsers.filter(
      (u) => u.email.toLowerCase().includes(q) || u.name.toLowerCase().includes(q),
    );
  }, [staffUsers, assigneeSearch]);

  const handleAddReminder = useCallback(async () => {
    if (!title.trim() || !dueDate || !assigneeId.trim()) {
      toast({ variant: 'error', title: 'Please fill in all required fields' });
      return;
    }

    try {
      await createReminder.mutateAsync({
        title: title.trim(),
        description: description.trim() || undefined,
        dueAt: new Date(`${dueDate}T${dueTime}:00`).toISOString(),
        assigneeId: assigneeId.trim(),
      });
      setTitle('');
      setDescription('');
      setDueDate('');
      setDueTime('09:00');
      setAssigneeId('');
      setAssigneeSearch('');
      setShowAddForm(false);
      toast({ variant: 'success', title: 'Reminder created' });
    } catch {
      toast({ variant: 'error', title: 'Failed to create reminder' });
    }
  }, [title, description, dueDate, dueTime, assigneeId, createReminder, toast]);

  const handleComplete = useCallback(
    async (reminderId: string) => {
      try {
        await completeReminder.mutateAsync(reminderId);
      } catch {
        toast({ variant: 'error', title: 'Failed to complete reminder' });
      }
    },
    [completeReminder, toast],
  );

  return (
    <Card>
      <CardHeader
        title="Reminders"
        action={
          can(PERMISSIONS.NOTE_CATEGORIES_CREATE) ? (
            <Button size="sm" variant="outline" onClick={() => setShowAddForm((prev) => !prev)}>
              {showAddForm ? 'Cancel' : 'Add Reminder'}
            </Button>
          ) : undefined
        }
      />
      <CardContent>
        {/* Add Reminder Form */}
        {showAddForm && (
          <div className="mb-4 rounded-lg border border-border p-4 dark:border-border-dark">
            <div className="space-y-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Follow up on tax documents"
                  className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add additional details or context..."
                  rows={3}
                  maxLength={2000}
                  className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400"
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Due Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Due Time
                  </label>
                  <input
                    type="time"
                    value={dueTime}
                    onChange={(e) => setDueTime(e.target.value)}
                    className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>
              </div>
              <div className="relative">
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Assign To <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={assigneeSearch}
                  onChange={(e) => {
                    setAssigneeSearch(e.target.value);
                    setShowAssigneeDropdown(true);
                    if (!e.target.value.trim()) setAssigneeId('');
                  }}
                  onFocus={() => setShowAssigneeDropdown(true)}
                  placeholder="Search by name or email..."
                  className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400"
                  autoComplete="off"
                />
                {showAssigneeDropdown && filteredUsers.length > 0 && (
                  <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-border bg-white shadow-lg dark:border-border-dark dark:bg-gray-800">
                    {filteredUsers.slice(0, 20).map((user) => (
                      <li key={user.id}>
                        <button
                          type="button"
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                          onClick={() => {
                            setAssigneeId(user.id);
                            setAssigneeSearch(user.email);
                            setShowAssigneeDropdown(false);
                          }}
                        >
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {user.name || user.email}
                          </span>
                          {user.name && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {user.email}
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {showAssigneeDropdown && assigneeSearch.trim() && filteredUsers.length === 0 && (
                  <div className="absolute z-10 mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-500 shadow-lg dark:border-border-dark dark:bg-gray-800">
                    No users found
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  The staff member who will receive the reminder notification.
                </p>
              </div>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={handleAddReminder}
                  loading={createReminder.isPending}
                  disabled={!title.trim() || !dueDate || !assigneeId.trim()}
                >
                  Save Reminder
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Reminders List */}
        {isLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading reminders...</p>
        ) : reminders && reminders.length > 0 ? (
          <div className="space-y-4">
            {/* Pending */}
            {pendingReminders.length > 0 && (
              <div>
                <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Pending ({pendingReminders.length})
                </h3>
                <ul role="list" className="divide-y divide-border dark:divide-border-dark">
                  {pendingReminders.map((reminder) => (
                    <ReminderListItem
                      key={reminder.id}
                      reminder={reminder}
                      onComplete={() => handleComplete(reminder.id)}
                      isCompleting={completeReminder.isPending}
                    />
                  ))}
                </ul>
              </div>
            )}

            {/* Completed */}
            {completedReminders.length > 0 && (
              <div>
                <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Completed ({completedReminders.length})
                </h3>
                <ul role="list" className="divide-y divide-border dark:divide-border-dark">
                  {completedReminders.map((reminder) => (
                    <ReminderListItem key={reminder.id} reminder={reminder} />
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No reminders set. Add a reminder to stay on top of follow-ups.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function ReminderListItem({
  reminder,
  onComplete,
  isCompleting,
}: {
  reminder: Reminder;
  onComplete?: () => void;
  isCompleting?: boolean;
}) {
  const dueDate = new Date(reminder.dueAt);
  const now = new Date();
  const isOverdue = !reminder.isCompleted && dueDate < now;

  const formattedDue = dueDate.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <li className="flex items-center gap-3 py-3">
      {/* Completion checkbox */}
      {!reminder.isCompleted && onComplete && (
        <button
          type="button"
          onClick={onComplete}
          disabled={isCompleting}
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded border border-border text-transparent transition-colors hover:border-brand-500 hover:text-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark"
          aria-label={`Mark "${reminder.title}" as complete`}
        >
          <svg
            className="h-3 w-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </button>
      )}
      {reminder.isCompleted && (
        <span
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded border border-green-500 bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400"
          aria-hidden="true"
        >
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </span>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm ${
            reminder.isCompleted
              ? 'text-gray-400 line-through dark:text-gray-500'
              : 'text-gray-900 dark:text-gray-100'
          }`}
        >
          {reminder.title}
        </p>
        {reminder.description && (
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
            {reminder.description}
          </p>
        )}
        <div className="mt-0.5 flex items-center gap-2 text-xs">
          <span
            className={
              isOverdue
                ? 'font-medium text-red-600 dark:text-red-400'
                : 'text-gray-500 dark:text-gray-400'
            }
          >
            {isOverdue ? 'Overdue: ' : 'Due: '}
            {formattedDue}
          </span>
          {reminder.assigneeName && (
            <>
              <span className="text-gray-300 dark:text-gray-600">&middot;</span>
              <span className="text-gray-500 dark:text-gray-400">{reminder.assigneeName}</span>
            </>
          )}
        </div>
      </div>

      {/* Overdue badge */}
      {isOverdue && (
        <Badge variant="danger" size="sm">
          Overdue
        </Badge>
      )}
    </li>
  );
}

// ─── Dynamic Detail Section ───────────────────────────────────────────────────

function DynamicDetailSection({
  section,
  contact,
}: {
  section: FormSection;
  contact: { [key: string]: unknown };
}) {
  const visibleFields = section.fields.filter((f) => f.is_active);
  if (visibleFields.length === 0) return null;

  // Get field values from form_data JSONB (single source of truth) + fallbacks
  const formDataRaw = contact.formData || contact.form_data;
  const savedFormData: Record<string, unknown> =
    typeof formDataRaw === 'string'
      ? JSON.parse(formDataRaw as string)
      : ((formDataRaw as Record<string, unknown> | undefined) ?? {});

  const fieldValues = visibleFields.map((field) => {
    let value: unknown;
    if (field.is_system_field && field.db_column) {
      // Try form_data (snake_case key) → contact object (camelCase)
      value =
        savedFormData[field.db_column] ??
        savedFormData[field.field_key] ??
        contact[camelCase(field.db_column)];
    } else {
      // Custom field — read from form_data by field_key
      value = savedFormData[field.field_key];

      // Fallback: formFieldValues array
      if (value === undefined) {
        const formFieldValues = contact.formFieldValues as
          | Array<{ fieldKey: string; value: unknown }>
          | undefined;
        if (formFieldValues) {
          const match = formFieldValues.find((fv) => fv.fieldKey === field.field_key);
          if (match) value = match.value;
        }
      }
    }

    // Format arrays for display
    if (Array.isArray(value)) {
      // String arrays (e.g., array of UUIDs for multi-select user fields)
      if (value.length > 0 && typeof value[0] === 'string') {
        // Leave as-is — the DetailRowWithLookup component will resolve to labels
        value = value.join(',');
      } else {
        // Object arrays (table fields) — join row values
        value = value
          .map((row: Record<string, unknown>) => Object.values(row).join(', '))
          .join(' | ');
      }
    }

    // Resolve select/radio field values to their display label
    if (
      value != null &&
      value !== '' &&
      (field.field_type === 'select' || field.field_type === 'radio') &&
      field.options &&
      field.options.length > 0
    ) {
      const matchedOption = field.options.find((opt) => opt.value === String(value));
      if (matchedOption) {
        value = matchedOption.label;
      }
    }

    // Resolve multi_select field values to their display labels
    if (
      value != null &&
      field.field_type === 'multi_select' &&
      field.options &&
      field.options.length > 0
    ) {
      const values =
        typeof value === 'string' ? value.split(',') : Array.isArray(value) ? value : [];
      const labels = values
        .map((v: string) => {
          const opt = field.options.find((o) => o.value === v.trim());
          return opt ? opt.label : v;
        })
        .filter(Boolean);
      value = labels.join(', ');
    }

    return {
      label: field.label,
      value: value != null && value !== '' ? String(value) : '—',
      field,
    };
  });

  // Tab sections don't show card header — title is already on the tab button
  if (section.render_as === 'tab') {
    return (
      <Card>
        <CardContent>
          <dl className="space-y-3">
            {fieldValues.map((item, idx) => (
              <DetailRowWithLookup
                key={idx}
                label={item.label}
                value={item.value}
                field={item.field}
              />
            ))}
          </dl>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title={section.name} />
      <CardContent>
        <dl className="space-y-3">
          {fieldValues.map((item, idx) => (
            <DetailRowWithLookup
              key={idx}
              label={item.label}
              value={item.value}
              field={item.field}
            />
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

function camelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

/**
 * Renders a detail row that resolves lookup-backed select field values (UUIDs) to their display labels.
 * For fields without a lookup_category, falls back to showing the raw value.
 */
function DetailRowWithLookup({
  label,
  value,
  field,
}: {
  label: string;
  value: string;
  field: FormField;
}) {
  const needsLookup =
    value !== '—' && field.lookup_category && ['select', 'multi_select'].includes(field.field_type);

  const { options } = useLookupValues(needsLookup ? field.lookup_category : null);

  // System user field — resolve the stored user id(s) to names.
  const isUserSelect = value !== '—' && field.field_type === 'user_select';
  const userRoleIds = (() => {
    const raw = field.validation_rules as Record<string, unknown> | undefined;
    const roles = raw?.roles;
    return Array.isArray(roles) ? (roles as string[]).filter(Boolean) : [];
  })();
  const { data: userOptions } = useQuery({
    queryKey: ['user-select-field', userRoleIds.slice().sort().join(',')],
    queryFn: async () => {
      const params = new URLSearchParams({ pageSize: '200', status: 'active' });
      if (userRoleIds.length > 0) params.set('role_ids', userRoleIds.join(','));
      const response = await apiClient.get<ApiResponse<Record<string, unknown>[]>>(
        `/users?${params.toString()}`,
      );
      return (response.data.data ?? []).map((u) => ({
        value: u.id as string,
        label: (u.name || u.email || u.id) as string,
      }));
    },
    staleTime: 2 * 60_000,
    enabled: isUserSelect,
  });

  let displayValue = value;
  if (needsLookup && options.length > 0) {
    // Check if value contains multiple IDs (comma-separated — from array fields)
    const hasMultiple = value.includes(',');
    if (hasMultiple || field.field_type === 'multi_select') {
      const values = value.split(',').map((v) => v.trim());
      displayValue = values.map((v) => options.find((o) => o.value === v)?.label || v).join(', ');
    } else {
      const match = options.find((o) => o.value === value);
      if (match) displayValue = match.label;
    }
  } else if (isUserSelect && userOptions && userOptions.length > 0) {
    displayValue = userOptions.find((o) => o.value === value)?.label || value;
  }

  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-sm text-right text-gray-900 dark:text-gray-100">{displayValue}</dd>
    </div>
  );
}

// ─── Jobs Tab Content ─────────────────────────────────────────────────────────

function ContactJobsTab({
  contactId,
  contactName,
}: {
  contactId: string;
  contactName: string;
  isClientNature: boolean;
}) {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [page, setPage] = useState(1);

  const { data: jobsData, isLoading } = useJobsList({
    clientId: contactId,
    page,
    pageSize: 20,
  });

  const jobs = jobsData?.items ?? [];
  const totalPages = jobsData ? Math.ceil(jobsData.total / jobsData.pageSize) : 1;

  const statusColors: Record<string, string> = {
    not_started: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    on_hold: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    waiting_on_client: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    in_review: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    complete: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  };

  const priorityColors: Record<string, string> = {
    low: 'text-gray-500',
    medium: 'text-blue-600 dark:text-blue-400',
    high: 'text-orange-600 dark:text-orange-400',
    urgent: 'text-red-600 dark:text-red-400',
  };

  const formatStatus = (status: string) =>
    status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <Card>
      <CardHeader
        title="Jobs"
        description={`Jobs associated with ${contactName}`}
        action={
          can(PERMISSIONS.JOBS_CREATE) ? (
            <Button size="sm" onClick={() => navigate(`/jobs/new?clientId=${contactId}`)}>
              Create Job
            </Button>
          ) : undefined
        }
      />
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
            <span className="ml-2 text-sm text-gray-500">Loading jobs...</span>
          </div>
        ) : jobs.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left dark:border-border-dark">
                    <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Title
                    </th>
                    <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Type
                    </th>
                    <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Status
                    </th>
                    <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Priority
                    </th>
                    <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Progress
                    </th>
                    <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Due Date
                    </th>
                    <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Assigned
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border dark:divide-border-dark">
                  {jobs.map((job) => (
                    <tr
                      key={job.id}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-surface-dark-secondary transition-colors"
                      onClick={() => navigate(`/jobs/${job.id}`)}
                    >
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {job.title}
                          </span>
                          {job.isOverdue && (
                            <span className="inline-flex items-center rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                              Overdue
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-gray-600 dark:text-gray-400">
                        {job.jobType ? formatStatus(job.jobType) : '—'}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            statusColors[job.status] || statusColors.not_started
                          }`}
                        >
                          {formatStatus(job.status)}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`text-xs font-medium ${priorityColors[job.priority] || ''}`}
                        >
                          {job.priority.charAt(0).toUpperCase() + job.priority.slice(1)}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                            <div
                              className="h-full rounded-full bg-brand-600"
                              style={{ width: `${job.progressPercentage}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{job.progressPercentage}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-gray-600 dark:text-gray-400">
                        {job.dueDate
                          ? new Date(job.dueDate).toLocaleDateString('en-CA', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : '—'}
                      </td>
                      <td className="px-3 py-3 text-gray-600 dark:text-gray-400">
                        {job.assignedManagerName ||
                          job.assignedPartnerName ||
                          job.assignedAccountantName ||
                          '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t border-border pt-3 dark:border-border-dark">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Page {page} of {totalPages} ({jobsData?.total ?? 0} jobs)
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="py-8 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              No jobs associated with this contact yet.
            </p>
            {can(PERMISSIONS.JOBS_CREATE) && (
              <Button
                size="sm"
                className="mt-3"
                onClick={() => navigate(`/jobs/new?clientId=${contactId}`)}
              >
                Create First Job
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
